import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faEye,
  faEyeSlash,
  faCheckCircle,
  faExclamationCircle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

const MicrosoftConnectorForm = ({ isOpen, onClose, onSave, editingConnector = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    instanceName: '',
    clientId: '',
    clientSecret: '',
    tenantId: '',
    userEmail: ''
  });

  const [showSensitive, setShowSensitive] = useState({
    clientSecret: false
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);

  // Populate form data when editing
  useEffect(() => {
    if (editingConnector && isOpen) {
      const credentials = editingConnector.credentials || {};
      setFormData({
        name: editingConnector.name || '',
        instanceName: editingConnector.instance_name || '',
        clientId: credentials.client_id || '',
        clientSecret: credentials.client_secret || '',
        tenantId: credentials.tenant_id || '',
        userEmail: credentials.user_email || ''
      });
    } else if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        name: '',
        instanceName: '',
        clientId: '',
        clientSecret: '',
        tenantId: '',
        userEmail: ''
      });
      setErrors({});
      setShowSensitive({ clientSecret: false });
    }
  }, [editingConnector, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData.name.trim()) newErrors.name = 'Connection name is required';
    if (!formData.clientId.trim()) newErrors.clientId = 'Client ID is required';
    if (!formData.clientSecret.trim()) newErrors.clientSecret = 'Client Secret is required';
    if (!formData.tenantId.trim()) newErrors.tenantId = 'Tenant ID is required';

    // Format validation
    if (formData.name && formData.name.length < 3) {
      newErrors.name = 'Connection name must be at least 3 characters';
    }

    // UUID format validation for Client ID and Tenant ID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    if (formData.clientId && !uuidRegex.test(formData.clientId)) {
      newErrors.clientId = 'Client ID should be a valid UUID format';
    }

    if (formData.tenantId && !uuidRegex.test(formData.tenantId)) {
      newErrors.tenantId = 'Tenant ID should be a valid UUID format';
    }

    // Email validation (optional field)
    if (formData.userEmail && !formData.userEmail.includes('@')) {
      newErrors.userEmail = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const toggleSensitive = (field) => {
    setShowSensitive(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleTestConnection = async () => {
    if (!validateForm()) {
      toast.error('Please fix validation errors before testing');
      return;
    }

    setTestingConnection(true);
    try {
      // Simulate API call for testing connection
      await new Promise(resolve => setTimeout(resolve, 2000));

      // TODO: Replace with actual Microsoft Graph API test call
      toast.success('Microsoft connection test successful!');
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix all errors before saving');
      return;
    }

    setIsLoading(true);
    try {
      const connectorData = {
        name: formData.name,
        instance_name: formData.instanceName || undefined,
        service_type: 'microsoft',
        auth_type: 'client_credentials',
        credentials: {
          client_id: formData.clientId,
          client_secret: formData.clientSecret,
          tenant_id: formData.tenantId,
          user_email: formData.userEmail || undefined
        }
      };

      await onSave(connectorData);
      toast.success(editingConnector ? 'Microsoft connector updated successfully!' : 'Microsoft connector created successfully!');
      handleClose();
    } catch (error) {
      toast.error('Failed to save connector');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      instanceName: '',
      clientId: '',
      clientSecret: '',
      tenantId: '',
      userEmail: ''
    });
    setErrors({});
    setShowSensitive({ clientSecret: false });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-600 to-cyan-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <FontAwesomeIcon icon={faMicrosoft} className="text-white text-lg sm:text-xl" />
                </div>
                <div>
                  <h2 className="text-base sm:text-xl font-semibold text-white">
                    {editingConnector ? 'Edit Microsoft Connector' : 'Microsoft Connector'}
                  </h2>
                  <p className="text-xs sm:text-sm text-white/80">
                    {editingConnector ? 'Update Azure AD authentication' : 'Configure Azure AD authentication for Microsoft 365'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-white/80 hover:text-white transition-colors p-1"
              >
                <FontAwesomeIcon icon={faTimes} className="text-lg sm:text-xl" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 max-h-[55vh] sm:max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Connection Name */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Connection Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`form-input w-full ${errors.name ? 'border-red-500' : ''}`}
                  placeholder="e.g., Microsoft 365 Production"
                />
                {errors.name && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-red-500 text-sm flex items-center"
                  >
                    <FontAwesomeIcon icon={faExclamationCircle} className="mr-1 text-xs" />
                    {errors.name}
                  </motion.p>
                )}
              </div>

              {/* Instance Name */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Instance Name <span className="text-gray-400">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.instanceName}
                  onChange={(e) => handleInputChange('instanceName', e.target.value)}
                  className="form-input w-full"
                  placeholder="e.g., production, dev"
                  disabled={!!editingConnector}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Unique identifier for multiple connectors
                </p>
              </div>
            </div>

            {/* Client ID */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Client ID (Application ID) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.clientId}
                onChange={(e) => handleInputChange('clientId', e.target.value)}
                className={`form-input w-full ${errors.clientId ? 'border-red-500' : ''}`}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              />
              {errors.clientId && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-500 text-sm flex items-center"
                >
                  <FontAwesomeIcon icon={faExclamationCircle} className="mr-1 text-xs" />
                  {errors.clientId}
                </motion.p>
              )}
            </div>

            {/* Client Secret */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Client Secret <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showSensitive.clientSecret ? 'text' : 'password'}
                  value={formData.clientSecret}
                  onChange={(e) => handleInputChange('clientSecret', e.target.value)}
                  className={`form-input w-full pr-10 ${errors.clientSecret ? 'border-red-500' : ''}`}
                  placeholder="Enter client secret"
                />
                <button
                  type="button"
                  onClick={() => toggleSensitive('clientSecret')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <FontAwesomeIcon
                    icon={showSensitive.clientSecret ? faEyeSlash : faEye}
                    className="text-sm"
                  />
                </button>
              </div>
              {errors.clientSecret && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-500 text-sm flex items-center"
                >
                  <FontAwesomeIcon icon={faExclamationCircle} className="mr-1 text-xs" />
                  {errors.clientSecret}
                </motion.p>
              )}
            </div>

            {/* Tenant ID */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Tenant ID (Directory ID) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.tenantId}
                onChange={(e) => handleInputChange('tenantId', e.target.value)}
                className={`form-input w-full ${errors.tenantId ? 'border-red-500' : ''}`}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              />
              {errors.tenantId && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-500 text-sm flex items-center"
                >
                  <FontAwesomeIcon icon={faExclamationCircle} className="mr-1 text-xs" />
                  {errors.tenantId}
                </motion.p>
              )}
            </div>

            {/* User Email (Optional) */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                User Email <span className="text-gray-400">(Optional)</span>
              </label>
              <input
                type="email"
                value={formData.userEmail}
                onChange={(e) => handleInputChange('userEmail', e.target.value)}
                className={`form-input w-full ${errors.userEmail ? 'border-red-500' : ''}`}
                placeholder="user@company.com"
              />
              {errors.userEmail && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-500 text-sm flex items-center"
                >
                  <FontAwesomeIcon icon={faExclamationCircle} className="mr-1 text-xs" />
                  {errors.userEmail}
                </motion.p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Used for user-delegated access scenarios
              </p>
            </div>

            {/* Helper Text */}
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">
                How to get Microsoft Azure credentials:
              </h4>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <li>1. Go to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="underline">Azure Portal</a></li>
                <li>2. Navigate to Azure Active Directory &rarr; App registrations</li>
                <li>3. Create new registration or select existing app</li>
                <li>4. Copy Application (client) ID and Directory (tenant) ID</li>
                <li>5. Go to Certificates & secrets &rarr; New client secret</li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:justify-between space-y-3 sm:space-y-0">
              <button
                onClick={handleTestConnection}
                disabled={testingConnection}
                className={`btn ${testingConnection ? 'btn-disabled' : 'btn-secondary'} w-full sm:w-auto`}
              >
                {testingConnection ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                    Test Connection
                  </>
                )}
              </button>

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={handleClose}
                  className="btn btn-secondary w-full sm:w-auto"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  className={`btn ${isLoading ? 'btn-disabled' : 'btn-primary'} w-full sm:w-auto`}
                >
                  {isLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                      {editingConnector ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingConnector ? 'Update Connector' : 'Create Connector'
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default MicrosoftConnectorForm;
